(() => {
  const CHARM_TYPE = 'Charm';
  const BAG_TYPE_FRAGMENT = 'bag';
  const PARENT_BAG_ID_PROP = '_parent_bag_id';
  const TOAST_ID = 'ff-cart-error-toast';
  const TOAST_VISIBLE_CLASS = 'ff-cart-toast--visible';
  const TOAST_DURATION_MS = 4000;

  let toastTimeout = null;

  function showToast(message) {
    const toast = document.getElementById(TOAST_ID);
    if (!toast) return;
    toast.textContent = message;
    requestAnimationFrame(() => toast.classList.add(TOAST_VISIBLE_CLASS));
    if (toastTimeout) clearTimeout(toastTimeout);
    toastTimeout = setTimeout(() => toast.classList.remove(TOAST_VISIBLE_CLASS), TOAST_DURATION_MS);
  }

  function isCharm(item) {
    return item.product_type === CHARM_TYPE;
  }

  function isBag(item) {
    return item.product_type.toLowerCase().includes(BAG_TYPE_FRAGMENT);
  }

  function getParentBagId(item) {
    return item.properties?.[PARENT_BAG_ID_PROP] ?? null;
  }

  function validateCharms(items) {
    const bagIdSet = new Set(
      items.filter(isBag).map((b) => b.properties?.['_bag_id']).filter(Boolean)
    );
    const seenParentIds = new Set();
    const toRemove = [];

    for (const charm of items.filter(isCharm)) {
      const parentId = getParentBagId(charm);
      if (!parentId || !bagIdSet.has(parentId) || seenParentIds.has(parentId)) {
        toRemove.push(charm.key);
        continue;
      }
      seenParentIds.add(parentId);
    }

    return toRemove;
  }

  async function removeItems(keys, sectionIds) {
    const body = { updates: Object.fromEntries(keys.map((k) => [k, 0])) };
    if (sectionIds) {
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  function applyCartSections(cartItems, parsedState) {
    cartItems.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      if (parsedState.sections?.[section.section]) {
        target.innerHTML = cartItems.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }
    });
    cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
  }

  async function syncCharms() {
    let cart;
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
    } catch {
      return;
    }

    const toRemove = validateCharms(cart.items);
    if (toRemove.length === 0) return;

    await removeItems(toRemove);
    showToast(
      toRemove.length === 1
        ? 'A charm has been removed from your cart as it requires a bag.'
        : `${toRemove.length} charms have been removed from your cart as they require a bag.`
    );

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ff-charms-cart' });
  }

  function interceptCheckout() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('#CartDrawer-Checkout, #checkout');
      if (!btn) return;

      let cart;
      try {
        const res = await fetch('/cart.js');
        cart = await res.json();
      } catch {
        return;
      }

      const toRemove = validateCharms(cart.items);
      if (toRemove.length === 0) return;

      e.preventDefault();
      e.stopPropagation();
      await removeItems(toRemove);
      showToast(
        toRemove.length === 1
          ? 'A charm has been removed from your cart as it requires a bag.'
          : `${toRemove.length} charms have been removed from your cart as they require a bag.`
      );

      // reload drawer so the Function doesn't block checkout on stale state
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ff-charms-cart' });
    }, true);
  }

  function observeCartContainer(id) {
    const target = document.getElementById(id);
    if (!target) return;

    let debounce = null;
    new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(syncCharms, 150);
    }).observe(target, { childList: true, subtree: true });
  }

  function observeCartDrawer() {
    observeCartContainer('CartDrawer-CartItems');
    observeCartContainer('main-cart-items');
  }

  let activeCharmRemove = null;

  class FfCharmRemove extends HTMLElement {
    connectedCallback() {
      this.querySelector('button').addEventListener('click', () => {
        activeCharmRemove = this;
      });
    }
  }

  document.addEventListener('click', async (e) => {
    const confirmBtn = e.target.closest('cart-remove-popup-button');
    if (!confirmBtn || !activeCharmRemove) return;

    const el = activeCharmRemove;
    activeCharmRemove = null;

    e.stopPropagation();

    const overlay = confirmBtn.closest('.general--popup__overlay');
    if (overlay) {
      overlay.style.display = 'none';
      overlay.classList.remove('opened');
    }

    const cartItems = el.closest('cart-drawer-items') || el.closest('cart-items');
    const isCartPage = cartItems?.tagName === 'CART-ITEMS';
    const line = el.dataset.bagLine;
    const linkedBagLine = el.dataset.linkedBagLine;
    const linkedBagKey = el.dataset.linkedBagKey;
    if (cartItems) {
      cartItems.enableLoading(line);
      if (linkedBagLine) cartItems.enableLoading(linkedBagLine);
    }

    try {
      const sectionIds = isCartPage ? cartItems.getSectionsToRender().map((s) => s.section) : null;

      let parsedState;
      if (linkedBagKey) {
        const rawProps = JSON.parse(el.dataset.linkedBagProps || '[]');
        const bagProps = Object.fromEntries(rawProps);
        bagProps['_bag_id'] = '';

        await removeItems([el.dataset.charmKey]);

        const body = { id: linkedBagKey, properties: bagProps };
        if (sectionIds) {
          body.sections = sectionIds;
          body.sections_url = window.location.pathname;
        }
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        parsedState = await res.json();
      } else {
        parsedState = await removeItems([el.dataset.charmKey], sectionIds);
      }

      if (isCartPage) {
        applyCartSections(cartItems, parsedState);
      } else {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ff-charms-cart' });
      }
    } finally {
      if (cartItems) {
        cartItems.disableLoading(line);
        if (linkedBagLine) cartItems.disableLoading(linkedBagLine);
      }
    }
  }, true);

  document.addEventListener('click', (e) => {
    if (e.target.closest('.popup--close')) activeCharmRemove = null;
  });

  let activePair = null;

  class FfCartRemovePair extends HTMLElement {
    connectedCallback() {
      this.querySelector('button').addEventListener('click', () => {
        activePair = this;
      });
    }
  }

  document.addEventListener('click', async (e) => {
    const confirmBtn = e.target.closest('cart-remove-popup-button');
    if (!confirmBtn || !activePair) return;

    const el = activePair;
    activePair = null;

    e.stopPropagation();

    const overlay = confirmBtn.closest('.general--popup__overlay');
    if (overlay) {
      overlay.style.display = 'none';
      overlay.classList.remove('opened');
    }

    const cartItems = el.closest('cart-drawer-items') || el.closest('cart-items');
    const isCartPage = cartItems?.tagName === 'CART-ITEMS';
    const line = el.dataset.index;
    const linkedCharmLine = el.dataset.linkedCharmLine;
    if (cartItems) {
      cartItems.enableLoading(line);
      if (linkedCharmLine) cartItems.enableLoading(linkedCharmLine);
    }

    try {
      const sectionIds = isCartPage ? cartItems.getSectionsToRender().map((s) => s.section) : null;
      const parsedState = await removeItems([el.dataset.ffBagKey, el.dataset.linkedCharmKey], sectionIds);
      if (isCartPage) {
        applyCartSections(cartItems, parsedState);
      } else {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ff-charms-cart' });
      }
    } finally {
      if (cartItems) {
        cartItems.disableLoading(line);
        if (linkedCharmLine) cartItems.disableLoading(linkedCharmLine);
      }
    }
  }, true);

  document.addEventListener('click', (e) => {
    if (e.target.closest('.popup--close')) activePair = null;
  });

  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('ff-charm-remove', FfCharmRemove);
    customElements.define('ff-cart-remove-pair', FfCartRemovePair);
    syncCharms();
    observeCartDrawer();
    interceptCheckout();
  });
})();
